import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';

import en from 'dashboard/i18n/locale/en';
import { brandTranslations } from 'shared/helpers/brandTranslations';
import * as Sentry from '@sentry/vue';
import {
  initializeAnalyticsEvents,
  initializeChatwootEvents,
} from 'dashboard/helper/scriptHelpers';
import App from '../v3/App.vue';
import router, { initalizeRouter } from '../v3/views/index';
import store from '../v3/store';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
// import { emitter } from '../shared/helpers/mitt';

// [VITE] This was added in https://github.com/chatwoot/chatwoot/commit/b57063a8b83c86819bd285f481298d7cd38ad50e
// Commenting it out for Vite migration
// Vue.config.env = process.env;

const localeModules = {
  ar: () => import('../dashboard/i18n/locale/ar/index.js'),
  bg: () => import('../dashboard/i18n/locale/bg/index.js'),
  ca: () => import('../dashboard/i18n/locale/ca/index.js'),
  cs: () => import('../dashboard/i18n/locale/cs/index.js'),
  da: () => import('../dashboard/i18n/locale/da/index.js'),
  de: () => import('../dashboard/i18n/locale/de/index.js'),
  el: () => import('../dashboard/i18n/locale/el/index.js'),
  es: () => import('../dashboard/i18n/locale/es/index.js'),
  et: () => import('../dashboard/i18n/locale/et/index.js'),
  fa: () => import('../dashboard/i18n/locale/fa/index.js'),
  fi: () => import('../dashboard/i18n/locale/fi/index.js'),
  fr: () => import('../dashboard/i18n/locale/fr/index.js'),
  he: () => import('../dashboard/i18n/locale/he/index.js'),
  hi: () => import('../dashboard/i18n/locale/hi/index.js'),
  hu: () => import('../dashboard/i18n/locale/hu/index.js'),
  id: () => import('../dashboard/i18n/locale/id/index.js'),
  it: () => import('../dashboard/i18n/locale/it/index.js'),
  ja: () => import('../dashboard/i18n/locale/ja/index.js'),
  ko: () => import('../dashboard/i18n/locale/ko/index.js'),
  lv: () => import('../dashboard/i18n/locale/lv/index.js'),
  ml: () => import('../dashboard/i18n/locale/ml/index.js'),
  nl: () => import('../dashboard/i18n/locale/nl/index.js'),
  no: () => import('../dashboard/i18n/locale/no/index.js'),
  pl: () => import('../dashboard/i18n/locale/pl/index.js'),
  pt: () => import('../dashboard/i18n/locale/pt/index.js'),
  pt_BR: () => import('../dashboard/i18n/locale/pt_BR/index.js'),
  ro: () => import('../dashboard/i18n/locale/ro/index.js'),
  ru: () => import('../dashboard/i18n/locale/ru/index.js'),
  sk: () => import('../dashboard/i18n/locale/sk/index.js'),
  sl: () => import('../dashboard/i18n/locale/sl/index.js'),
  sr: () => import('../dashboard/i18n/locale/sr/index.js'),
  sv: () => import('../dashboard/i18n/locale/sv/index.js'),
  ta: () => import('../dashboard/i18n/locale/ta/index.js'),
  th: () => import('../dashboard/i18n/locale/th/index.js'),
  tr: () => import('../dashboard/i18n/locale/tr/index.js'),
  uk: () => import('../dashboard/i18n/locale/uk/index.js'),
  uz: () => import('../dashboard/i18n/locale/uz/index.js'),
  vi: () => import('../dashboard/i18n/locale/vi/index.js'),
  zh_CN: () => import('../dashboard/i18n/locale/zh_CN/index.js'),
  zh_TW: () => import('../dashboard/i18n/locale/zh_TW/index.js'),
  is: () => import('../dashboard/i18n/locale/is/index.js'),
  lt: () => import('../dashboard/i18n/locale/lt/index.js'),
};
const selectedLocale = window.chatwootConfig.selectedLocale || 'en';
const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en: brandTranslations(en) },
});

const app = createApp(App);
app.use(i18n);
app.use(store);
app.use(router);

// Vue.use(VueRouter);
// Vue.use(VueI18n);
// Vue.prototype.$emitter = emitter;
app.component('fluent-icon', FluentIcon);

if (window.errorLoggingConfig) {
  Sentry.init({
    app,
    dsn: window.errorLoggingConfig,
    denyUrls: [
      // Chrome extensions
      /^chrome:\/\//i,
      /chrome-extension:/i,
      /extensions\//i,

      // Locally saved copies
      /file:\/\//i,

      // Safari extensions.
      /safari-web-extension:/i,
      /safari-extension:/i,
    ],
    integrations: [Sentry.browserTracingIntegration({ router })],
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
}

initializeChatwootEvents();
initializeAnalyticsEvents();
initalizeRouter();

const mountApp = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => app.mount('#app'), { once: true });
  } else {
    app.mount('#app');
  }
};

if (selectedLocale === 'en') {
  mountApp();
} else {
  const loadLocale = localeModules[selectedLocale];
  if (loadLocale) {
    loadLocale()
      .then(({ default: messages }) => {
        i18n.global.setLocaleMessage(selectedLocale, brandTranslations(messages));
        mountApp();
      })
      .catch(() => {
        window.chatwootConfig.selectedLocale = 'en';
        mountApp();
      });
  } else {
    window.chatwootConfig.selectedLocale = 'en';
    mountApp();
  }
}
